import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Renderer2,
  HostListener,
  EventEmitter,
  AfterViewInit, OnDestroy, ViewContainerRef, ViewChild, Output, Input
} from '@angular/core';
import { NotificationService } from '../../../core/services';
import { IOpenOrder } from '../core/interfaces/IOpenOrder';
import { forkJoin, Subscription } from 'rxjs';
import { DeliveryDocumentsWebSocketService } from '../core/services/DeliveryDocumentsWebSocketService';
import { Guid } from 'guid-typescript';
import { IGenerateOrderOperation } from '../core/interfaces/IGenerateOrderOperation';
import { ISignalRResponse } from '../../../core/interfaces/ISignalRResponse';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { IClosedShopOrders, OpenOrdersService } from '../core/services/OpenOrdersService';
import { IGenerateBatchOperation } from '../core/interfaces/IGenerateBatchOperation';
import { IBatchDocumentUrl } from '../core/interfaces/IBatchDocumentUrl';
import Files from '../../../core/utils/files';
import { OpenOrdersServiceErrorResolverService } from '../core/services/error-resolvers/open-orders-service-error-resolver.service';
import { finalize, map } from 'rxjs/operators';
import { GenerateDeliverySlipsModalComponent } from '../generate-delivery-slips-modal/generate-delivery-slips-modal.component';
import { FilterOperator } from '../../../core/types';
import { environment } from '../../../../environments/environment';
import { ActivatedRoute } from '@angular/router';
import { InvoiceTypes } from '../../../core/enums/InvoiceTypes';
import { IDictionary } from '../../../core/interfaces/IDictionary';

@Component({
    selector: 'app-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ListComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('progressBar') progressbarModalContent;

  @Output() reloadBatchDocumentsList = new EventEmitter<void>();
  @Output() loaded = new EventEmitter<number>();
  @Output() stats = new EventEmitter<{ count: number; averageDurationSeconds: number | null; excludedFromAverage: number; date?: Date | string | null; }>();
  @Input() set closedOrders(val) {
    this.onlyClosed = val === true;
    if (this.onlyClosed) {
      this.orderStatus = 7;
      if (!this.selectedDate) {
        this.selectedDate = new Date();
      }
    }
  }
  public onlyClosed = false;
  public data: IOpenOrder[];
  public tableMaxWidth: string;
  public tablePageSize = 50;
  public loading = false;
  public allDocumentsReady = false;
  public documentsAreProcessing = false;
  public checked = false;
  public indeterminate = false;
  public setOfCheckedId = new Set<number>();
  public pdfLink;
  public orderStatus = 0;
  public numberOfPositions: string;
  public showOnlyWithComments = false;
  private _lastActionId: string;
  public percentDone: number;
  public selectedDate: Date | string | null = null;
  public selectedDeliveryMethod: string = null;
  public selectedProcessors: string[] = [];
  public processors: string[] = [];
  public readonly dateFormat = environment.defaultDateFormat;
  public readonly storageRange: { start: string; end: string; } = {
    start: null,
    end: null
  };
  public readonly numberOfPositionsOptions = [
    {
      label: 'kleine (< 5)',
      value: 'lt:5'
    },
    {
      label: 'große (>= 5)',
      value: 'ge:5'
    },
    {
      label: 'ein Artikel (= 1)',
      value: 'eq:1'
    }
  ];
  public readonly deliveryMethods: string[];

  private _numberOfDoneOperations: number;
  private _numberOfAllOperations: number;
  private _progressModal: NzModalRef;
  private _subscriptions: Subscription[] = [];
  private _sortKey: string | null = null;
  private _sortOrder: string | null = null;
  private _loadSeq = 0;


  constructor(
    private _service: OpenOrdersService,
    private _cdr: ChangeDetectorRef,
    private _notify: NotificationService,
    private renderer: Renderer2,
    private _errorResolver: OpenOrdersServiceErrorResolverService,
    private _wsService: DeliveryDocumentsWebSocketService,
    private _modal: NzModalService,
    private _viewContainerRef: ViewContainerRef,
    private _activatedRoute: ActivatedRoute,
  ) {
    this.deliveryMethods = (this._activatedRoute.snapshot.data['deliveryMethods'] as IDictionary).value.split('; ');
  }

  ngOnInit(): void {
    this.load();
    this.initWs();
  }

  onRefreshList(): void {
    this.load();
  }

  @HostListener('window:resize')
  onResize() {
    this.setTableMaxHeight();
  }

  ngAfterViewInit(): void {
    this.setTableMaxHeight();
  }

  private setTableMaxHeight(): void {
    setTimeout(() => this.tableMaxWidth = this.renderer.selectRootElement('nz-table', true).offsetWidth + 'px');
  }

  private load(): void {
    const seq = ++this._loadSeq;
    this.loading = true;

    let numberOfPositions: { value: number; operator: FilterOperator; } = null;

    if (this.numberOfPositions) {
      const [operator, value] = this.numberOfPositions.split(':') as [FilterOperator, number];
      numberOfPositions = {
        value,
        operator
      };
    }

    const request = this.onlyClosed
      ? this._service.getClosedOrders(
        this.selectedDate,
        this.selectedDeliveryMethod,
        numberOfPositions,
        this.storageRange,
        this.selectedProcessors
      )
      : this._service.getOpenOrders(
        this.orderStatus,
        this.selectedDate,
        this.selectedDeliveryMethod,
        numberOfPositions,
        this.storageRange
      ).pipe(map(items => this.openOrdersEnvelope(items)));

    this._subscriptions.push(
      request.pipe(
        map(response => ({
          ...response,
          items: this.filterComments(response.items)
        }))
      ).subscribe(res => {
        if (seq !== this._loadSeq) {
          return;
        }
        if (this.onlyClosed && this.dropUnknownProcessors(res.processors)) {
          this.load();
          return;
        }

        this.loading = false;
        this.processors = res.processors ?? [];
        this.data = res.items;
        this.tablePageSize = Math.max(this.data.length, 1);
        if (this.onlyClosed && !this._sortKey) {
          this._sortKey = 'processingFinishedAt';
          this._sortOrder = 'descend';
        }
        this.applyClientSort();
        this.loaded.emit(this.data.length);
        this.stats.emit(this.headingStats(res));
        this.setOfCheckedId.clear();
        this.refreshCheckedStatus();
        this._cdr.detectChanges();
      }, error => {
        if (seq !== this._loadSeq) {
          return;
        }
        this.parseError(error);
      })
    );
  }

  private openOrdersEnvelope(items: IOpenOrder[]): IClosedShopOrders {
    return {
      items,
      processors: [],
      count: items.length,
      averageDurationSeconds: null,
      excludedFromAverage: 0
    };
  }

  private filterComments(items: IOpenOrder[]): IOpenOrder[] {
    if (!this.showOnlyWithComments) {
      return items;
    }
    return items.filter(order => (order.comment || order.customerComment || order.internalComment));
  }

  private dropUnknownProcessors(processors: string[]): boolean {
    const allowed = new Set((processors ?? []).map(name => name.trim().toLowerCase()));
    const next = this.selectedProcessors.filter(name => allowed.has(name.trim().toLowerCase()));
    if (next.length === this.selectedProcessors.length) {
      return false;
    }
    this.selectedProcessors = next;
    return true;
  }

  private headingStats(res: IClosedShopOrders): { count: number; averageDurationSeconds: number | null; excludedFromAverage: number; date?: Date | string | null; } {
    if (this.onlyClosed && !this.showOnlyWithComments) {
      return {
        count: res.count,
        averageDurationSeconds: res.averageDurationSeconds,
        excludedFromAverage: res.excludedFromAverage,
        date: this.selectedDate
      };
    }

    return {
      count: this.data.length,
      averageDurationSeconds: this.calcAverageDuration(),
      excludedFromAverage: this.onlyClosed ? this.countExcluded() : 0,
      date: this.selectedDate
    };
  }

  private countExcluded(): number {
    return this.data.filter(order => !this.hasDuration(order.processingDurationSeconds)).length;
  }

  private hasDuration(duration: number | null | undefined): boolean {
    return duration !== null && duration !== undefined && duration >= 0;
  }

  private calcAverageDuration(): number | null {
    const durations = this.data
      .map(order => order.processingDurationSeconds)
      .filter((duration): duration is number => this.hasDuration(duration));

    if (!durations.length) {
      return null;
    }

    return Math.round(durations.reduce((sum, duration) => sum + duration, 0) / durations.length);
  }

  updateCheckedSet(id: number, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
  }

  onItemChecked(id: number, checked: boolean): void {
    this.updateCheckedSet(id, checked);
    this.refreshCheckedStatus();
  }

  onAllChecked(value: boolean): void {
    this.data.forEach(item => this.updateCheckedSet(item.id, value));
    this.refreshCheckedStatus();
  }

  get selectedCount(): number {
    return this.setOfCheckedId.size;
  }

  refreshCheckedStatus(): void {
    this.checked = this.data.length > 0 && this.data.every(item => this.setOfCheckedId.has(item.id));
    this.indeterminate = this.data.some(item => this.setOfCheckedId.has(item.id)) && !this.checked;
    const checkedItems = this.getCheckedItems();
    const filteredItems = checkedItems.filter(value => (value.isDeliveryNoteGenerated && (!value.isInvoiceRequired || (value.isInvoiceRequired && value.isInvoiceGenerated))));
    this.allDocumentsReady = filteredItems.length > 0 && checkedItems.length > 0 && checkedItems.length === filteredItems.length;
  }

  public regenerateDocument(id: number): void {
    this.loading = true;
    this._service.refreshDeliveryNote(id.toString()).subscribe(this.onRefreshList.bind(this), error =>  {
      this.loading = false;
      this._notify.pushError('Fehlgeschlagen', this._errorResolver.getMessage(error.response.errorCode ?? ''));
    });
  }

  public openGenerateDeliverySlipsModal(modal: GenerateDeliverySlipsModalComponent) {
    modal.orders = this.getCheckedItems().filter(
      order => (
        (order.deliveryMethod?.toUpperCase() === 'UPS' || order.deliveryMethod?.toUpperCase() === 'DHL') && order.trackingCode === '') ||
      order.deliveryMethod === null );
    modal.showModal();
  }

  public onDateChange(): void {
    if (this.onlyClosed && !this.selectedDate) {
      this.selectedDate = new Date();
    }
    this.onRefreshList();
  }

  public onTableQueryParams(params: NzTableQueryParams): void {
    const currentSort = params.sort.find(item => item.value !== null);
    const sortKey = currentSort?.key ?? null;
    const sortOrder = currentSort?.value ?? null;
    if (sortKey === this._sortKey && sortOrder === this._sortOrder) {
      return;
    }
    this._sortKey = sortKey;
    this._sortOrder = sortOrder;
    this.applyClientSort();
    this._cdr.detectChanges();
  }

  private applyClientSort(): void {
    if (!this.data?.length || !this._sortKey || !this._sortOrder) {
      return;
    }
    const dir = this._sortOrder === 'ascend' ? 1 : -1;
    const key = this._sortKey;
    this.data = [...this.data].sort((left, right) =>
      dir * this.compareSortValues(this.sortValue(left, key), this.sortValue(right, key))
    );
  }

  private sortValue(order: IOpenOrder, key: string): unknown {
    if (key === 'fullDate') {
      return order.fullDate;
    }
    return (order as unknown as Record<string, unknown>)[key];
  }

  private compareSortValues(left: unknown, right: unknown): number {
    if (left == null && right == null) {
      return 0;
    }
    if (left == null) {
      return -1;
    }
    if (right == null) {
      return 1;
    }
    if (typeof left === 'number' && typeof right === 'number') {
      return left - right;
    }
    if (typeof left === 'boolean' && typeof right === 'boolean') {
      return Number(left) - Number(right);
    }
    const leftTime = Date.parse(String(left));
    const rightTime = Date.parse(String(right));
    if (!Number.isNaN(leftTime) && !Number.isNaN(rightTime) && String(left).length > 8) {
      return leftTime - rightTime;
    }
    return String(left).localeCompare(String(right), 'de', { numeric: true });
  }

  public storageRangeChanged(): void {
    if ((this.storageRange.start && this.storageRange.end) || (!this.storageRange.start && !this.storageRange.end)) {
      this.onRefreshList();
    }
  }

  public setUncompletedStatusForOrdersWithComment(): void {
    const apiCalls = this.getCheckedItems()
      .filter(x => x.customerComment)
      .map(x => this._service.setUncompletedOrder(x.id.toString(), 'Bestellung hat Kommentar'));

    let count = 0;

    apiCalls.forEach(apiCall => {
      apiCall
        .pipe(
          finalize(() => {
            count++;

            if (count === apiCalls.length) {
              this.onRefreshList();
            }
          })
        )
        .subscribe({
          error: this.parseError.bind(this),
        });
    });
  }

  generateBatchDocument(): void {
    this.documentsAreProcessing = true;
    this._lastActionId = Guid.create().toString();
    const ids = this.getCheckedItems().map((value) => (value.id));
    const batch: IGenerateBatchOperation = {
      operationId: this._lastActionId,
      shopProviderKey: this.getCheckedItems()[0].shopProviderKey,
      orderIds: ids
    };
    this._numberOfDoneOperations = 0;
    this._numberOfAllOperations = 1;
    this.showProgressModal();
    this._service.generateBatchDocument(batch).subscribe(value => {
    }, this.parseError);
  }

  generateRequiredDocuments(): void {
    this.documentsAreProcessing = true;
    this._lastActionId =  Guid.create().toString();
    const ordersToGenerate = this.getCheckedItems().map(value => ({
      operationId: this._lastActionId,
      orderId: value.id,
      shopProviderKey: value.shopProviderKey
    } as IGenerateOrderOperation));
    this._numberOfDoneOperations = 0;
    this._numberOfAllOperations = ordersToGenerate.length;
    this.showProgressModal();
    this._service.generateDocuments(ordersToGenerate).subscribe(value => {
    }, this.parseError);
  }

  private initWs(): void {
    this._wsService.start();
    this._wsService.onWaitForGeneratedDocuments((response: ISignalRResponse<IOpenOrder>) => {
      if (response.hasError) {
        this._notify.pushError('Fehlgeschlagen', this._errorResolver.getMessage(response.errorCode ?? ''));
        this.resetDocumentProcess();
        return;
      }
      if (response.operationId === this._lastActionId) {
        const curEl = this.data.find(value => value.id === response.response.id);
        if (curEl) {
          this._numberOfDoneOperations++;
          curEl.isInvoiceGenerated = response.response.isInvoiceGenerated;
          curEl.isDeliveryNoteGenerated = response.response.isDeliveryNoteGenerated;
          this.refreshCheckedStatus();
          this.reCalcProgressBar();
        }
        if (this._numberOfDoneOperations === this._numberOfAllOperations && this._progressModal) {
          this._notify.pushSuccess('Fertig!', 'Erforderliche Dokumente erzeugt');
          this.onRefreshList();
          this.resetDocumentProcess();
        }
        this._cdr.detectChanges();
      }
    });

    this._wsService.onWaitForBatchDocument((response: ISignalRResponse<IBatchDocumentUrl>) => {
      if (response.hasError) {
        this._notify.pushError('Fehlgeschlagen', this._errorResolver.getMessage(response.errorCode ?? ''));
        this.resetDocumentProcess();
        return;
      }
      if (response.response.partiallyGenerated) {
        this._notify.pushError('Fehlgeschlagen', this._errorResolver.getMessage('PartialDocument'));
      }
      if (response.operationId === this._lastActionId) {
          this._numberOfDoneOperations++;
          this.refreshCheckedStatus();
          this.reCalcProgressBar();
        if (this._numberOfDoneOperations === this._numberOfAllOperations && this._progressModal) {
          this._notify.pushSuccess('Fertig!', 'Batch-Dokument erzeugt');
          this.resetDocumentProcess();
          this.openBatchFile(response.response.url);
          this.load();
          this.reloadBatchDocumentsList.emit();
        }
        this._cdr.detectChanges();
      }
    });
  }

  private parseError = data => {
    this.resetDocumentProcess();
    this._notify.pushError('Fehlgeschlagen', this._errorResolver.getMessage((data && data.InternalCode) ? data.InternalCode : ''));
  }


  private getCheckedItems(): IOpenOrder[] {
    return this.data.filter(({id}) => this.setOfCheckedId.has(id));
  }

  private showProgressModal() {
    this._progressModal = this._modal.create({
      nzTitle: 'Dokumente bestellen',
      nzContent: this.progressbarModalContent,
      nzViewContainerRef: this._viewContainerRef,
      nzFooter: [
        {
          label: 'Abbrechen',
          onClick: () => {
            this.resetDocumentProcess();
          }
        }
      ]
    });
  }

  private reCalcProgressBar() {
    this.percentDone = Math.round(this._numberOfDoneOperations / this._numberOfAllOperations * 100);
    this._cdr.detectChanges();
  }

  private resetDocumentProcess() {
    if (this._progressModal) {
      this._progressModal.destroy();
      this._progressModal = null;
    }
    this.percentDone = 0;
    this.documentsAreProcessing = false;
    this._numberOfAllOperations = 0;
    this._numberOfDoneOperations = 0;
    this._cdr.detectChanges();
  }

  private openBatchFile(pdfLink: string) {
    Files.openByUrl(pdfLink, link => {
      this.pdfLink = pdfLink;
    });
  }

  formatCartons(cartons: { name: string; quantity: number }[] | undefined): string {
    if (!cartons?.length) {
      return '—';
    }
    return cartons.map(c => `${c.quantity}× ${c.name}`).join(', ');
  }

  public ngOnDestroy(): void {
    this._wsService.stop();
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }
}
